import "./profile_checklist.scss";
import { Body } from "@sprout/typography.tsx";
import { SecondaryButton, TertiaryButton } from "@sprout/buttons";
import { useSafePostHog } from "@/hooks/use_posthog";
import { usePage } from "@inertiajs/react";
import CheckMarkCircleFilledIcon from "@sprout/icons/check_mark_circle_filled.tsx";
import MinusCircleIcon from "@sprout/icons/minus_circle.tsx";
import CircleIcon from "@sprout/icons/circle.tsx";
import {
  type ChecklistItem,
  type ChecklistStatus,
} from "@components/profile/profile_checklist_items.ts";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";

const StatusIcon = ({ status }: { status: ChecklistStatus }) => {
  if (status === "complete") return <CheckMarkCircleFilledIcon size="md" />;
  if (status === "partial") return <MinusCircleIcon size="md" />;
  return <CircleIcon size="md" />;
};

type RowProps = {
  item: ChecklistItem;
  t: (key: string) => string;
  onEdit: () => void;
};

const ProfileChecklistRow = ({ item, t, onEdit }: RowProps) => {
  const posthog = useSafePostHog();
  const { trackEvent } = useSnowplowTracker();
  const { workPreferencesExperimentEnabled } = usePage<{
    workPreferencesExperimentEnabled?: boolean;
  }>().props;
  const { key, status, value } = item;
  const secondLine = value || t(`${key}.description`);

  const handleClick = (button: "add" | "edit") => {
    posthog?.capture("profile_button_clicked", { button, section: key, source: "dashboard" });
    trackEvent(SnowplowEvent.ProfileWidgetButtonClicked, {
      section: key,
      button: button,
      variant: workPreferencesExperimentEnabled ? "profile_b" : "profile_a",
    });
    onEdit();
  };

  return (
    <li className="profile_checklist__row">
      <span className="profile_checklist__row-icon">
        <StatusIcon status={status} />
      </span>
      <div className="profile_checklist__row-text">
        <Body medium>{t(`${key}.title`)}</Body>
        <Body metadata secondary className="profile_checklist__row-description">
          {secondLine}
        </Body>
      </div>
      <div className="profile_checklist__row-action">
        {status === "not_started" ? (
          <SecondaryButton size="medium" onClick={() => handleClick("add")}>
            {t("add")}
          </SecondaryButton>
        ) : (
          <TertiaryButton secondary onClick={() => handleClick("edit")}>
            {t("edit")}
          </TertiaryButton>
        )}
      </div>
    </li>
  );
};

export default ProfileChecklistRow;
